import { Stack, Typography } from "@mui/material";
import { FC } from "react";
import { CellProps, Column, Row } from "react-table";

import { getCustomers } from "api/customers";
import { customersKeys } from "api/customers/queries";
import { CustomersType } from "api/customers/types";
import { QueryParamsType } from "api/utils";
import { hasPermissions } from "components/stores/UserStore";
import Table from "components/Table";
import useRemoteTableLogic from "components/Table/useRemoteTableLogic";
import UserAvatar from "components/UserAvatar/UserAvatar";
import { useModal } from "utils/hooks/useModal";

import DeleteCustomerModal from "./components/DeleteCustomerModal";
import EditCustomerModal from "./components/EditCustomerModal";
import CustomersActionsFormatter from "./formatters/CustomersActionsFormatter";
import { useCustomersFilter } from "./useCustomersFilter";

interface CustomersTableProps {
  filters?: QueryParamsType<CustomersType>;
}

const CustomersTable: FC<CustomersTableProps> = ({
  filters: filtersFromProps,
}) => {
  const filterHook = useCustomersFilter();
  const filters = filtersFromProps ?? filterHook.filters;

  const { data, status, sortOptions, paginationOptions } = useRemoteTableLogic(
    customersKeys.list(),
    getCustomers,
    filters,
  );

  const {
    isOpen: isOpenDelete,
    handleClose: handleCloseDelete,
    handleOpen: handleOpenDelete,
    context: deleteContext,
  } = useModal<CustomersType>();
  const {
    isOpen: isEditOpen,
    handleClose: handleCloseEdit,
    handleOpen: handleOpenEdit,
    context: editContext,
  } = useModal<CustomersType>();

  const columns: Column<CustomersType>[] = [
    {
      id: "name",
      Header: "Name",
      Cell: ({ row }: CellProps<CustomersType>) => (
        <Stack direction="row" alignItems="center">
          <UserAvatar
            size={36}
            fontSize={16}
            userName={[row.original.firstName, row.original.lastName].join(" ")}
          />
          <Typography ml={1}>
            {row.original.firstName} {row.original.lastName}
          </Typography>
        </Stack>
      ),
    },
    {
      accessor: "email",
      Header: "Email",
    },
    {
      accessor: "social",
      Header: "Social",
    },
    {
      accessor: "address",
      Header: "Address",
    },
    {
      accessor: "city",
      Header: "City",
    },
    {
      accessor: "state",
      Header: "State",
    },

    {
      id: "actions",
      Header: "Actions",
      Cell: ({ cell }: CellProps<CustomersType>) => (
        <CustomersActionsFormatter
          {...cell}
          handleOpenEdit={handleOpenEdit}
          handleOpenDelete={handleOpenDelete}
        />
      ),
      align: "right",
    },
  ];

  return (
    <Stack spacing={2} flex={1} height="100%" overflow="hidden">
      <Table
        data={data}
        columns={columns}
        sort={sortOptions}
        pagination={paginationOptions}
        status={status}
        getRowProps={(row: Row<CustomersType>) => {
          let backgroundColor;
          if (row.original.cbcErrorMessage) {
            backgroundColor = "#fff2f2";
          }

          return {
            sx: { backgroundColor },
          };
        }}
      />
      {hasPermissions("delete:customers") && (
        <>
          <DeleteCustomerModal
            isOpen={isOpenDelete}
            handleClose={handleCloseDelete}
            customer={deleteContext}
          />
        </>
      )}
      {hasPermissions("update:customers") && (
        <>
          <EditCustomerModal
            isOpen={isEditOpen}
            handleClose={handleCloseEdit}
            customer={editContext}
          />
        </>
      )}
    </Stack>
  );
};

export default CustomersTable;
